/**
 * /dila/item/:handle — D.I.L.A. dedicated single-product page (shareable).
 *
 * Stays in the sun-faded patina theme. Shows one product with size selection,
 * a "Frequently Bought Together" bundle nudge (10% outfit discount), a
 * "Complete the Fit" rail (other pieces in the same wash, up to 4), and a
 * "You May Also Like" rail (the same silhouette in the other washes).
 * Always offers a one-tap return to the full wash rack.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Clock, ShoppingBag, Plus, Share2 } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import DilaFooter from '../components/DilaFooter';
import { toast } from 'sonner';

const TYPE_LABEL = { set: 'Sweatsuit Set', hoodie: 'Cropped Hoodie', tee: 'Lux Tee', sweats: 'Cropped Sweats', hat: 'Bucket Hat' };
const SIZE_ORDER = ['2T', '4T', '5-6 years', '6', '7-8 years', '8', '9-10 years', 'S/M', 'L/XL', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL'];
const sizeRank = (s) => { const i = SIZE_ORDER.indexOf(s); return i < 0 ? 999 : i; };

const sizeKeyFor = (p) => {
  const o = (p.options || []).find((opt) => opt.name !== 'Color');
  return o ? o.name : 'Size';
};

const coreType = (p) => {
  const n = (p.name || '').toLowerCase();
  const cat = (p.category || '').toLowerCase();
  if (cat === 'accessories' || n.includes('hat') || n.includes('bucket') || n.includes('beanie') || n.includes('cap')) return 'hat';
  if (cat === 'suits' || n.includes('sweatsuit') || n.includes('set')) return 'set';
  if (n.includes('hoodie')) return 'hoodie';
  if (n.includes('tee')) return 'tee';
  if (n.includes('sweats') || cat === 'bottoms') return 'sweats';
  return 'other';
};

const colorOf = (p) => {
  const colors = Array.from(new Set((p.variants || []).map((v) => v.options?.Color).filter(Boolean)));
  return colors[0] || null;
};

// A concise, in-theme size-ladder for a product (all variants of its single wash).
const sizeRows = (p) => {
  const sk = sizeKeyFor(p);
  return (p.variants || [])
    .map((v) => ({ size: v.options?.[sk], variantId: v.id, available: v.available, price: v.price, image: v.image }))
    .filter((s) => s.size)
    .sort((a, b) => sizeRank(a.size) - sizeRank(b.size));
};

const DilaItem = () => {
  const { handle } = useParams();
  const { cart, addManyToCart, goToCheckout, loading: cartLoading } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState('');
  const [discount, setDiscount] = useState({ code: null, percentage: 10 });
  const [bundleSizes, setBundleSizes] = useState({});   // { handle: size }
  const [bundlePicked, setBundlePicked] = useState({});  // { handle: bool }

  useEffect(() => {
    Promise.all([
      api.get('/shopify/collections/dila-1/full').then((r) => r.data?.collection?.products || []).catch(() => []),
      api.get('/shopify/dila/discount').then((r) => r.data).catch(() => null),
    ]).then(([prods, disc]) => {
      setProducts(prods);
      if (disc?.code) setDiscount({ code: disc.code, percentage: disc.percentage });
    }).finally(() => setLoading(false));
  }, [handle]);

  useEffect(() => { setSize(''); setBundleSizes({}); setBundlePicked({}); }, [handle]);

  const product = useMemo(() => products.find((p) => p.handle === handle), [products, handle]);
  const type = product ? coreType(product) : null;
  const wash = product ? colorOf(product) : null;
  const rows = useMemo(() => (product ? sizeRows(product) : []), [product]);

  // Same wash, other silhouettes — "Complete the Fit" (up to 4).
  const sameWash = useMemo(() => {
    if (!product) return [];
    return products
      .filter((p) => p.handle !== handle && colorOf(p) === wash && coreType(p) !== 'other')
      .slice(0, 4);
  }, [products, product, wash, handle]);

  // Same silhouette, other washes — "You May Also Like".
  const otherWashes = useMemo(() => {
    if (!product) return [];
    return products.filter((p) => p.handle !== handle && coreType(p) === type && colorOf(p) !== wash);
  }, [products, product, type, wash, handle]);

  // Frequently-bought-together: this item + up to 2 complementary same-wash pieces.
  const fbt = useMemo(() => sameWash.slice(0, 2), [sameWash]);

  const chosenVariant = () => rows.find((r) => r.size === size);
  const inCart = (variantId) => (cart?.lineItems || []).some((li) => li.variantId === variantId);

  const addThis = async () => {
    const v = chosenVariant();
    if (!v) { toast.error('Pick a size first'); return; }
    if (!v.available) { toast.error('That size is sold out'); return; }
    const ok = await addManyToCart([{ variantId: v.variantId, quantity: 1 }]);
    if (ok) toast.success('Added to your rack');
  };

  const heroImage = (product?.images?.[0]?.url) || product?.image || rows.find((r) => r.image)?.image;
  const gallery = (product?.images || []).map((i) => i.url).filter(Boolean).slice(0, 4);

  // Bundle add — this item (selected size) + any picked FBT items (their chosen/first size).
  const bundleLines = () => {
    const lines = [];
    const v = chosenVariant();
    if (v && v.available) lines.push({ variantId: v.variantId, quantity: 1 });
    fbt.forEach((p) => {
      if (bundlePicked[p.handle] === false) return; // default picked
      const pr = sizeRows(p);
      const chosen = bundleSizes[p.handle] || (pr.find((s) => s.available)?.size);
      const row = pr.find((s) => s.size === chosen);
      if (row && row.available) lines.push({ variantId: row.variantId, quantity: 1 });
    });
    return lines;
  };

  const bundleTotal = () => {
    let t = 0;
    const v = chosenVariant();
    if (v) t += parseFloat(v.price || 0);
    fbt.forEach((p) => {
      if (bundlePicked[p.handle] === false) return;
      const pr = sizeRows(p);
      const chosen = bundleSizes[p.handle] || (pr.find((s) => s.available)?.size);
      const row = pr.find((s) => s.size === chosen);
      if (row) t += parseFloat(row.price || 0);
    });
    return t;
  };

  const addBundle = async () => {
    const lines = bundleLines();
    if (lines.length < 2) { toast.error('Pick your size to build the bundle'); return; }
    const ok = await addManyToCart(lines);
    if (ok) toast.success(`${lines.length} pieces added — ${Math.round(discount.percentage)}% off at checkout`);
  };

  const share = async () => {
    const url = window.location.href;
    const payload = { title: product?.name || 'D.I.L.A.', text: 'Slow-cured apparel by D.I.L.A.', url };
    try {
      if (navigator.share) { await navigator.share(payload); return; }
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (e) { /* user cancelled */ }
  };

  const cartCount = (cart?.lineItems || []).reduce((t, i) => t + i.quantity, 0);

  return (
    <div
      className="min-h-screen text-stone-900"
      style={{
        background:
          'radial-gradient(circle at 25% 15%, #f7dfa9 0%, transparent 50%), radial-gradient(circle at 75% 75%, #c87f4a 0%, transparent 60%), linear-gradient(135deg, #f3e6cd 0%, #e0b780 50%, #a86838 100%)',
      }}
      data-testid="dila-item-page"
    >
      <div className="fixed inset-0 pointer-events-none opacity-25 mix-blend-multiply z-0"
        style={{ backgroundImage: 'repeating-linear-gradient(40deg, transparent 0 10px, rgba(0,0,0,0.04) 10px 11px)' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Crumb */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/matrix" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60 flex items-center gap-2">
            <ArrowLeft className="w-3 h-3" /> PRSVR · Matrix
          </Link>
          <Link to="/dila" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60">
            Design House
          </Link>
        </div>

        {loading ? (
          <div className="py-32 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : !product ? (
          <div className="py-32 text-center">
            <h2 className="text-3xl font-black uppercase mb-4">This piece has cleared the manifest.</h2>
            <Link to="/dila" className="underline text-sm uppercase tracking-widest">Return to the collection</Link>
          </div>
        ) : (
          <>
            {/* Back to wash */}
            {wash && (
              <Link
                to={`/dila/${handle}?color=${wash}`}
                className="inline-flex items-center gap-2 border-2 border-stone-900 bg-white/40 backdrop-blur px-4 py-2 mb-8 text-[10px] tracking-[0.25em] uppercase font-black hover:bg-stone-900 hover:text-amber-50 transition-colors"
                data-testid="dila-item-back-to-wash"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Wash {wash} — Build the Full Fit
              </Link>
            )}

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
              {/* Gallery */}
              <div>
                <div className="relative overflow-hidden bg-stone-200 shadow-[0_20px_40px_-15px_rgba(120,60,20,0.4)]" data-testid="dila-item-hero">
                  {heroImage && <img src={heroImage} alt={product.name} className="w-full h-auto block" />}
                  {wash && (
                    <div className="absolute top-3 left-3 bg-stone-900/85 text-amber-50 text-[10px] tracking-[0.25em] font-black px-2.5 py-1 backdrop-blur-sm">
                      WASH {wash}
                    </div>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {gallery.map((g, i) => (
                      <div key={i} className="bg-stone-200 overflow-hidden aspect-square">
                        <img src={g} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Details */}
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase font-black mb-3 opacity-70">
                  D.I.L.A. · {type && TYPE_LABEL[type] ? TYPE_LABEL[type] : 'Slow-Cured Apparel'}
                </div>
                <h1 className="text-4xl sm:text-5xl font-black uppercase leading-[0.9] tracking-tight mb-4" data-testid="dila-item-name">
                  {product.name}
                </h1>
                <div className="text-2xl font-black mb-6" data-testid="dila-item-price">
                  ${parseFloat(rows[0]?.price || product.price || 0).toFixed(0)}
                </div>

                <div className="inline-flex items-center gap-2 border-2 border-stone-900 bg-white/30 backdrop-blur px-4 py-2 mb-6">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-[10px] tracking-[0.25em] uppercase font-black">Cure Window · Up To 14 Days</span>
                </div>

                {product.description && (
                  <p className="text-sm leading-relaxed opacity-80 mb-6 max-w-md">{product.description}</p>
                )}

                {/* Size selector */}
                <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-2 opacity-70">Select Size</div>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full max-w-xs border-2 border-stone-900/40 bg-white/70 px-3 py-3 text-sm font-bold uppercase tracking-wider focus:border-stone-900 outline-none mb-4"
                  data-testid="dila-item-size"
                >
                  <option value="">Choose your size</option>
                  {rows.map((r) => (
                    <option key={r.variantId} value={r.size} disabled={!r.available}>
                      {r.size}{!r.available ? ' — sold out' : ''}
                    </option>
                  ))}
                </select>

                <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                  <button
                    onClick={addThis}
                    disabled={cartLoading}
                    className="flex-1 flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-6 py-4 text-xs font-black tracking-[0.25em] uppercase hover:bg-stone-800 transition-colors disabled:opacity-40"
                    data-testid="dila-item-add"
                  >
                    {cartLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to Rack
                  </button>
                  <button
                    onClick={share}
                    className="flex items-center justify-center gap-2 border-2 border-stone-900 px-5 py-4 text-xs font-black tracking-[0.2em] uppercase hover:bg-stone-900 hover:text-amber-50 transition-colors"
                    data-testid="dila-item-share"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>

                {cartCount > 0 && (
                  <button
                    onClick={() => goToCheckout(cartCount >= 2 ? discount.code : null)}
                    className="mt-3 max-w-md w-full flex items-center justify-center gap-2 border-2 border-stone-900 px-6 py-3 text-[11px] font-black tracking-[0.25em] uppercase hover:bg-stone-900 hover:text-amber-50 transition-colors"
                    data-testid="dila-item-checkout"
                  >
                    Checkout ({cartCount}) <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Frequently Bought Together */}
            {fbt.length > 0 && (
              <div className="mt-16 border-2 border-stone-900 bg-white/40 backdrop-blur p-6 lg:p-8" data-testid="dila-item-fbt">
                <div className="text-[10px] tracking-[0.35em] uppercase font-black mb-1 opacity-70">Frequently Bought Together</div>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-5">Complete The {wash} Fit · Save {Math.round(discount.percentage)}%</h2>

                <div className="flex flex-wrap items-stretch gap-3">
                  {/* This item chip */}
                  <div className="flex-1 min-w-[160px] border-2 border-stone-900 bg-white/60 p-3">
                    <div className="aspect-square bg-stone-200 overflow-hidden mb-2">
                      {heroImage && <img src={heroImage} alt={product.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="text-xs font-black uppercase leading-tight">{TYPE_LABEL[type] || product.name}</div>
                    <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">This item · Size {size || '—'}</div>
                  </div>

                  {fbt.map((p) => {
                    const pr = sizeRows(p);
                    const t = coreType(p);
                    const img = (p.images?.[0]?.url) || p.image || pr.find((s) => s.image)?.image;
                    const picked = bundlePicked[p.handle] !== false;
                    const chosen = bundleSizes[p.handle] || (pr.find((s) => s.available)?.size) || '';
                    return (
                      <div key={p.handle} className={`flex-1 min-w-[160px] border-2 p-3 ${picked ? 'border-stone-900 bg-white/60' : 'border-stone-900/30 bg-white/30 opacity-60'}`} data-testid={`dila-fbt-${t}`}>
                        <Link to={`/dila/item/${p.handle}`} className="block aspect-square bg-stone-200 overflow-hidden mb-2">
                          {img && <img src={img} alt={p.name} className="w-full h-full object-cover hover:opacity-80 transition-opacity" />}
                        </Link>
                        <div className="text-xs font-black uppercase leading-tight">{TYPE_LABEL[t] || p.name}</div>
                        <select
                          value={chosen}
                          onChange={(e) => setBundleSizes((m) => ({ ...m, [p.handle]: e.target.value }))}
                          className="w-full border-2 border-stone-900/30 bg-white/70 px-2 py-1.5 text-[11px] font-bold uppercase mt-2 focus:border-stone-900 outline-none"
                          data-testid={`dila-fbt-size-${t}`}
                        >
                          {pr.map((s) => (
                            <option key={s.variantId} value={s.size} disabled={!s.available}>{s.size}{!s.available ? ' — out' : ''}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 mt-2 text-[10px] font-black uppercase tracking-widest cursor-pointer">
                          <input
                            type="checkbox"
                            checked={picked}
                            onChange={(e) => setBundlePicked((m) => ({ ...m, [p.handle]: e.target.checked }))}
                            className="accent-stone-900"
                            data-testid={`dila-fbt-check-${t}`}
                          />
                          {picked ? 'In bundle' : 'Add'}
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-5 border-t-2 border-stone-900/20">
                  <div className="text-sm">
                    <span className="uppercase tracking-widest text-[10px] font-black opacity-60">Bundle Total</span>
                    <span className="ml-3 font-black text-xl">${bundleTotal().toFixed(2)}</span>
                    <span className="ml-2 text-[11px] font-bold text-emerald-800 uppercase">− {Math.round(discount.percentage)}% at checkout</span>
                  </div>
                  <button
                    onClick={addBundle}
                    disabled={cartLoading}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-6 py-3.5 text-xs font-black tracking-[0.25em] uppercase hover:bg-stone-800 transition-colors disabled:opacity-40"
                    data-testid="dila-item-add-bundle"
                  >
                    <ShoppingBag className="w-4 h-4" /> Add Bundle To Rack
                  </button>
                </div>
              </div>
            )}

            {/* Complete the Fit */}
            {sameWash.length > 0 && (
              <div className="mt-16" data-testid="dila-item-complete-fit">
                <div className="text-[10px] tracking-[0.35em] uppercase font-black mb-5 opacity-70">Complete The Fit · Wash {wash}</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {sameWash.map((p) => <ItemCard key={p.handle} p={p} />)}
                </div>
              </div>
            )}

            {/* You May Also Like */}
            {otherWashes.length > 0 && (
              <div className="mt-16" data-testid="dila-item-also-like">
                <div className="text-[10px] tracking-[0.35em] uppercase font-black mb-5 opacity-70">
                  You May Also Like · {TYPE_LABEL[type] || 'More'} In Other Washes
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {otherWashes.map((p) => <ItemCard key={p.handle} p={p} />)}
                </div>
              </div>
            )}

            <DilaFooter />
          </>
        )}
      </div>
    </div>
  );
};

const ItemCard = ({ p }) => {
  const t = coreType(p);
  const wash = colorOf(p);
  const rows = sizeRows(p);
  const img = (p.images?.[0]?.url) || p.image || rows.find((r) => r.image)?.image;
  return (
    <Link to={`/dila/item/${p.handle}`} className="group block border-2 border-stone-900/30 bg-white/40 backdrop-blur hover:border-stone-900 transition-colors" data-testid={`dila-rec-${p.handle}`}>
      <div className="aspect-square bg-stone-200 overflow-hidden">
        {img && <img src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
      </div>
      <div className="p-3">
        <div className="text-xs font-black uppercase leading-tight">{TYPE_LABEL[t] || p.name}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">
          Wash {wash} · ${parseFloat(rows[0]?.price || p.price || 0).toFixed(0)}
        </div>
      </div>
    </Link>
  );
};

export default DilaItem;
